import { FieldValues, Path, useFormContext } from "react-hook-form";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./form";
import { RequiredAsterisk } from "./required-asterisk";

interface FieldSelectProps<TFormValues extends FieldValues> {
  name: Path<TFormValues>;
  label: string;
  options: { value: string; label: string }[];
  isDisabled?: boolean;
  hasRequiredAsterisk?: boolean;
  title?: string;
  unstyle?: boolean;
}

export function FieldSelect<TFormValues extends FieldValues>({
  name,
  label,
  options = [], // Default empty array
  isDisabled = false,
  hasRequiredAsterisk = false,
  title = "Select an option",
  unstyle = false,
}: FieldSelectProps<TFormValues>) {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        // Find matching option
        const selectedOption = options?.find(
          (option) => String(option.value) === String(field.value),
        );

        return (
          <FormItem
            className={unstyle ? "flex w-full flex-col gap-1 !space-y-0" : ""}
          >
            <FormLabel className={unstyle ? "text-gray-500" : ""}>
              {label}
            </FormLabel>
            {hasRequiredAsterisk && <RequiredAsterisk />}
            <Select
              onValueChange={field.onChange}
              value={field.value || ""} // Ensure string value
              disabled={isDisabled}
            >
              <FormControl>
                {/* @ts-expect-error - unstyle is not a valid prop for SelectPrimitive.Trigger, but we want to allow it */}
                <SelectTrigger title={title} unstyle={unstyle}>
                  {selectedOption?.label ||
                    (field.value ? String(field.value) : "Select...")}
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {options?.length > 0 ? (
                  options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no option" disabled>
                    No options available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
